import React, { useContext } from "react";
import { SettingsContext } from "./SettingsContext.tsx";
import { SelectButton } from "primereact/selectbutton";
import { RadioButton } from "primereact/radiobutton";
import {
    RoundingComparingDecimalOptions,
    RoundingComparingDigitOptions,
    RoundingComparingOptions,
    RoundingDisplayOptions,
    RoundingPlaceOptions,
} from "../Constants.ts";
import { withLabels } from "../Labels.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";

/**
 * What the sheet asks, and about what numbers.
 *
 * Rounding and comparing sit under one format the way Place Value's three
 * question types do. The rounding-place control is hidden for a comparing
 * sheet, and its options switch between whole-number places and tenth/whole
 * depending on whether the numbers carry a decimal point.
 *
 * The display control (ADR 0046) is basic rather than advanced, on the same
 * reasoning ADR 0023 §2 already gives TellingTime's own display picker: this
 * is the format's own value panel, and that panel is basic whole. It stays
 * visible regardless of the question type, since it applies to both
 * (ADR 0046 §1) - unlike the rounding-place control, whose availability
 * genuinely depends on the question.
 */
export function RoundingComparingSettings() {
    const { settings, setSettings } = useContext(SettingsContext);

    const isDecimal = settings.RoundingComparingDecimals > 0;
    const showPlace = settings.RoundingComparingQuestion !== RoundingComparingOptions.Comparing;

    const placeOptions = (isDecimal
        ? [RoundingPlaceOptions.Tenth, RoundingPlaceOptions.Whole, RoundingPlaceOptions.Mixed]
        : [
              RoundingPlaceOptions.Ten,
              RoundingPlaceOptions.Hundred,
              RoundingPlaceOptions.Thousand,
              RoundingPlaceOptions.Mixed,
          ]) as string[];
    const t = useTranslation();

    function set(patch) {
        setSettings({ ...settings, ...patch });
    }

    return (
        <div className="roundingComparingSettingsContainer">
            <div className="regroupingContainer">
                <SelectButton
                    value={settings.RoundingComparingQuestion}
                    onChange={(e) => e.value && set({ RoundingComparingQuestion: e.value })}
                    options={withLabels(Object.values(RoundingComparingOptions), settings.Language)}
                    className="select regrouping"
                    aria-label={t('settings.questionType')}
                />
            </div>

            <div className="regroupingContainer">
                <span className="settingLabel">{t('settings.showAsLabel')}</span>
                <SelectButton
                    value={settings.RoundingDisplay}
                    onChange={(e) => e.value && set({ RoundingDisplay: e.value })}
                    options={withLabels(Object.values(RoundingDisplayOptions), settings.Language)}
                    className="select regrouping"
                    aria-label={t('settings.roundingDisplayAria')}
                />
            </div>

            <div className="selectContainer">
                <span className="settingLabel">{t('settings.numbersLabel')}</span>
            </div>
            <div className="radioButtons" role="radiogroup" aria-label={t('settings.decimalPlacesAria')}>
                {RoundingComparingDecimalOptions.map((option) => (
                    <div key={option}>
                        <RadioButton
                            inputId={`roundingComparingDecimals-${option}`}
                            value={option}
                            checked={settings.RoundingComparingDecimals === option}
                            onChange={(e) => set({ RoundingComparingDecimals: Number(e.target.value) })}
                        />
                        <label htmlFor={`roundingComparingDecimals-${option}`}>
                            {option === 0 ? t('settings.whole') : `${option} ${t('settings.decimalPlacesAbbrev')}`}
                        </label>
                    </div>
                ))}
            </div>

            {!isDecimal && (
                <>
                    <div className="selectContainer">
                        <span className="settingLabel">{t('settings.numberOfDigitsLabel')}</span>
                    </div>
                    <div className="radioButtons" role="radiogroup" aria-label={t('settings.numberOfDigitsAria')}>
                        {RoundingComparingDigitOptions.map((option) => (
                            <div key={option}>
                                <RadioButton
                                    inputId={`roundingComparingDigits-${option}`}
                                    value={option}
                                    checked={settings.RoundingComparingDigits === option}
                                    onChange={(e) =>
                                        set({ RoundingComparingDigits: Number(e.target.value) })
                                    }
                                />
                                <label htmlFor={`roundingComparingDigits-${option}`}>{option}</label>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {showPlace && (
                <div className="regroupingContainer">
                    <SelectButton
                        value={settings.RoundingPlace}
                        onChange={(e) => e.value && set({ RoundingPlace: e.value })}
                        options={withLabels(placeOptions, settings.Language)}
                        className="select regrouping"
                        aria-label={t('settings.roundingPlaceAria')}
                    />
                </div>
            )}
        </div>
    );
}

export default RoundingComparingSettings;
